import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LogOut, User, BarChart2, Monitor, SlidersHorizontal } from 'lucide-react';
import { Container } from '@/components/layout/Container';
import { Section } from '@/components/layout/Section';
import { Heading } from '@/components/layout/Heading';
import { useAuth } from '@/contexts/AuthContext';
import { ROUTES } from '@/routes';

export function AccountLayout() {
  const { t } = useTranslation('account');
  const { signOut } = useAuth();
  const navigate = useNavigate();

  async function onSignOut() {
    await signOut();
    void navigate(ROUTES.home, { replace: true });
  }

  const tabs = [
    { to: ROUTES.account, end: true, icon: User, label: t('nav.profile') },
    { to: ROUTES.accountStats, end: false, icon: BarChart2, label: t('nav.stats') },
    { to: ROUTES.accountDevices, end: false, icon: Monitor, label: t('nav.devices') },
    { to: ROUTES.accountPreferences, end: false, icon: SlidersHorizontal, label: t('nav.preferences') },
  ] as const;

  return (
    <Section padding="md">
      <Container size="md">
        <header className="mb-6 flex items-end justify-between gap-4">
          <div>
            <Heading level={1} className="text-3xl sm:text-4xl">
              {t('page.title')}
            </Heading>
            <p className="mt-2 text-sm text-muted-foreground">{t('page.subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={() => { void onSignOut(); }}
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground transition-colors duration-fast ease-out-quart hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            {t('actions.sign_out')}
          </button>
        </header>

        <nav aria-label={t('page.title')} className="mb-6 flex border-b border-border">
          {tabs.map(({ to, end, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `-mb-px inline-flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors duration-fast ease-out-quart ${
                  isActive
                    ? 'border-brand text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand'
                    : 'border-transparent text-muted-foreground hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand'
                }`
              }
            >
              <Icon className="h-4 w-4" aria-hidden="true" />
              {label}
            </NavLink>
          ))}
        </nav>

        <Outlet />
      </Container>
    </Section>
  );
}
